<template>
  <component
    :is="tag"
    :type="tag === 'button' ? btnType : undefined"
    class="fts-btn-lite"
    :class="[
      `fts-btn-lite--${type}`,
      `fts-btn-lite--${size}`,
      {
        'fts-btn-lite--floating': floating,
        'fts-btn-lite--no-padding': noPadding,
        'fts-btn-lite--error': error && !isDisabled,
        'fts-btn-lite--loading': loading,
        'fts-btn-lite--disabled': isDisabled,
      }
    ]"
    :disabled="tag === 'button' ? isDisabled : undefined"
    @click="$emit('click', $event)"
  >
    <div class="fts-btn-lite__loader" v-if="loading">
      <fts-spinner :size="spinnerSize" />
    </div>

    <span class="fts-btn-lite__icon" v-if="$slots['icon-left'] && !floating">
      <slot name="icon-left" />
    </span>

    <span class="fts-btn-lite__label" v-if="!floating">
      <slot />
    </span>

    <span class="fts-btn-lite__icon" v-if="floating">
      <slot />
    </span>

    <span class="fts-btn-lite__icon" v-if="$slots['icon-right'] && !floating">
      <slot name="icon-right" />
    </span>
  </component>
</template>

<script>
import FTSSpinner from "./FTSSpinner";

export default {
  components: {
    "fts-spinner": FTSSpinner,
  },
  props: {
    tag: {
      type: String,
      default: "button",
      validator: (v) => ["button", "a"].includes(v),
    },
    btnType: {
      type: String,
      default: "button",
      validator: (v) => ["button", "submit"].includes(v),
    },
    type: {
      type: String,
      default: "contained",
      validator: (v) => ["contained", "outlined", "text"].includes(v),
    },
    size: {
      type: String,
      default: "md",
      validator: (v) => ["lg", "md", "sm"].includes(v),
    },
    floating: Boolean,
    noPadding: Boolean,
    error: Boolean,
    disabled: Boolean,
    loading: Boolean,
  },
  emits: ["click"],
  computed: {
    isDisabled() {
      return this.disabled || this.loading;
    },
    spinnerSize() {
      return this.size === "lg" ? 16 : this.size === "sm" ? 12 : 14;
    },
  },
};
</script>

<style lang="scss">
@use "sass:map";
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;
@use "./src/assets/scss/tokens" as tokens;

@function primary($shade) {
  @return map.get(map.get(tokens.$colors, "primary"), $shade);
}
@function danger($shade) {
  @return map.get(map.get(tokens.$colors, "red"), $shade);
}

// ── Size tokens ─────────────────────────────────────────────
$btn-lite-sizes: (
  lg: (height: 44px, padding-y: 8px, padding-x: 16px, gap: 8px,
       icon: 24px, font-size: 1.125rem, line-height: 1.75rem,
       floating-size: 44px, no-padding-floating-size: 32px),
  md: (height: 40px, padding-y: 8px, padding-x: 12px, gap: 4px,
       icon: 20px, font-size: 1rem,     line-height: 1.5rem,
       floating-size: 40px, no-padding-floating-size: 28px),
  sm: (height: 36px, padding-y: 8px, padding-x: 12px, gap: 0px,
       icon: 16px, font-size: 0.875rem, line-height: 1.25rem,
       floating-size: 36px, no-padding-floating-size: 24px),
);

.fts-btn-lite {
  @include user-select-none;
  @include flex-center-middle;
  position: relative;
  border: none;
  border-radius: 4px;
  outline: none;
  background-color: transparent;
  font-family: var(--fts-font-family);
  font-weight: 500;
  white-space: nowrap;
  text-decoration: none;
  box-sizing: border-box;
  cursor: pointer;
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;

  // ── Sizes ──────────────────────────────────────────────────
  @each $name, $s in $btn-lite-sizes {
    &--#{$name} {
      height: map.get($s, height);
      padding: map.get($s, padding-y) map.get($s, padding-x);
      gap: map.get($s, gap);
      font-size: map.get($s, font-size);
      line-height: map.get($s, line-height);

      // text button always uses 16px horizontal padding
      &.fts-btn-lite--text:not(.fts-btn-lite--no-padding) {
        padding-left: 1rem;
        padding-right: 1rem;
      }

      .fts-btn-lite__icon {
        width: map.get($s, icon);
        height: map.get($s, icon);
      }

      // floating (icon-only)
      &.fts-btn-lite--floating {
        width: map.get($s, floating-size);
        height: map.get($s, floating-size);
        padding: 0;
        gap: 0;
        flex-shrink: 0;
      }

      // no-padding floating
      &.fts-btn-lite--floating.fts-btn-lite--no-padding {
        width: map.get($s, no-padding-floating-size);
        height: map.get($s, no-padding-floating-size);
      }

      // no-padding text (no horizontal padding)
      &.fts-btn-lite--no-padding:not(.fts-btn-lite--floating) {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }

  // ── Contained ──────────────────────────────────────────────
  &--contained {
    background-color: primary(500);
    color: #fff;
  }

  // ── Outlined ───────────────────────────────────────────────
  &--outlined {
    border: 1px solid primary(500);
    color: primary(500);
    background-color: #fff;
  }

  // ── Text ───────────────────────────────────────────────────
  &--text {
    color: var(--fts-gray-700);
  }

  // ── Hover: universal across all types ─────────────────────
  &:hover:not(.fts-btn-lite--disabled):not(.fts-btn-lite--error) {
    background-color: primary(100);
    border-color: primary(500);
    color: primary(500);
  }

  // ── Pressed: universal across all types ───────────────────
  &:active:not(.fts-btn-lite--disabled):not(.fts-btn-lite--error) {
    background-color: primary(700);
    border-color: primary(700);
    color: #fff;
  }

  // ── Error ──────────────────────────────────────────────────
  &--error {
    &.fts-btn-lite--contained {
      background-color: danger(100);
      color: var(--fts-danger);
    }
    &.fts-btn-lite--outlined {
      background-color: #fff;
      border: 1px solid var(--fts-danger);
      color: var(--fts-danger);
    }
    &.fts-btn-lite--text {
      color: var(--fts-danger);
    }
  }

  // ── Disabled ───────────────────────────────────────────────
  &--disabled,
  &:disabled {
    cursor: not-allowed;
    pointer-events: none;

    &.fts-btn-lite--contained {
      background-color: var(--fts-gray-200);
      color: var(--fts-gray-500);
      border-color: transparent;
    }
    &.fts-btn-lite--outlined {
      background-color: #fff;
      border-color: var(--fts-gray-200);
      color: var(--fts-gray-300);
    }
    &.fts-btn-lite--text {
      background-color: transparent;
      color: var(--fts-gray-300);
    }
  }

  // ── Loading ────────────────────────────────────────────────
  &--loading {
    cursor: not-allowed;
    pointer-events: none;
  }
  &__loader {
    @include position($top: 0, $left: 0, $right: 0, $bottom: 0);
    @include flex-center-middle;
    @include theme() {
      background-color: rgba($color: theme-get('fts-gray-100'), $alpha: 0.75);
    }
    svg { margin: 0; }
  }

  // ── Icon slot ──────────────────────────────────────────────
  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg, img, i {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  &__label {
    display: inline-block;
  }

  &:focus-visible {
    outline: 2px solid primary(500);
    outline-offset: 2px;
  }
}
</style>
